
import React, { useState, useEffect, useCallback } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { List, Card, Typography, Button, Checkbox, Tag, Space, Tooltip, Divider } from 'antd';
import { EditOutlined, ClockCircleOutlined, DeleteOutlined, UndoOutlined } from '@ant-design/icons';
import { Task } from '../../types/task';
import api from '../../lib/api';
import { usePomodoro } from '../../context/PomodoroContext';

const { Text } = Typography;

type DraggableTaskItemProps = {
  task: Task;
  index: number;
  moveTask: (dragIndex: number, hoverIndex: number) => void;
  onToggleComplete: (taskId: number) => void;
  onDelete: (taskId: number) => void;
  onRestore: (taskId: number) => void;
  onEdit: (task: Task) => void;
  onSelectForPomodoro: (taskId: number, taskTitle: string) => void;
};

type ItemType = {
  id: string;
  index: number;
};

const TaskItem: React.FC<DraggableTaskItemProps> = ({ 
  task, 
  index, 
  moveTask, 
  onToggleComplete, 
  onDelete,
  onRestore,
  onEdit,
  onSelectForPomodoro 
}) => {
  const { state } = usePomodoro();
  const isCurrentPomodoroTask = state.currentTaskId === task.id;
  const ref = React.useRef<HTMLDivElement>(null);
  
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'TASK',
    item: { id: task.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => !task.completed,
  }));
  
  const [, drop] = useDrop<ItemType>({
    accept: 'TASK',
    hover: (item, monitor) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      
      // Don't allow completed tasks to be a drop target
      if (task.completed) {
        return;
      }
      
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;
      
      // Only perform the move when the mouse has crossed half of the items height
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      
      moveTask(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });
  
  drag(drop(ref));
  
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <div
      ref={ref}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: task.completed ? 'default' : 'move',
        marginBottom: '8px',
      }}
    >
      <Card
        size="small"
        style={{ 
          backgroundColor: task.completed ? '#f9f9f9' : 'white',
          borderLeft: isCurrentPomodoroTask && !task.completed ? '3px solid #1890ff' : undefined
        }}
        styles={{ body: { padding: '8px 12px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Checkbox 
              checked={task.completed}
              onChange={() => onToggleComplete(task.id)}
              style={{ marginRight: '12px' }}
            />
            <div>
              {task.category && (
                <Tag color="blue" style={{ marginBottom: '4px' }}>
                  {task.category}
                </Tag>
              )}
              <div>
                {task.project && (
                  <span
                    className="mr-2 inline-block h-2 w-2 rounded-full align-middle"
                    style={{ background: task.project.color }}
                    title={task.project.name}
                  />
                )}
                <Text
                  style={{
                    textDecoration: task.completed ? 'line-through' : 'none',
                    color: task.completed ? '#888' : 'rgba(0, 0, 0, 0.85)',
                    fontWeight: isCurrentPomodoroTask && !task.completed ? 'bold' : 'normal',
                  }}
                >
                  {task.title}
                  {isCurrentPomodoroTask && !task.completed && (
                    <Tag color="green" style={{ marginLeft: '8px' }}>专注中</Tag>
                  )}
                </Text>
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {formatTime(task.created_at)}
              </Text>
            </div>
          </div>
          
          <Space>
            {!task.completed ? (
              <>
                <Tooltip title="编辑">
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<EditOutlined />} 
                    onClick={() => onEdit(task)}
                  />
                </Tooltip>
                <Tooltip title="开始专注">
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<ClockCircleOutlined />}
                    onClick={() => onSelectForPomodoro(task.id, task.title)}
                    style={{ color: '#52c41a' }}
                  />
                </Tooltip>
              </>
            ) : (
              <Tooltip title="恢复">
                <Button 
                  type="text" 
                  size="small" 
                  icon={<UndoOutlined />} 
                  onClick={() => onRestore(task.id)}
                  style={{ color: '#faad14' }}
                />
              </Tooltip>
            )}
            <Tooltip title="删除">
              <Button 
                type="text" 
                size="small" 
                icon={<DeleteOutlined />} 
                onClick={() => onDelete(task.id)}
                danger
              />
            </Tooltip>
          </Space>
        </div>

        {task.subtasks.length > 0 && (
          <ul className="ml-6 mt-2 space-y-1 border-l border-gray-100 pl-3">
            {task.subtasks.map((subtask) => (
              <li key={subtask.id} className="flex items-center gap-2 text-sm text-gray-600">
                <Checkbox
                  checked={subtask.completed}
                  onChange={() => onToggleComplete(subtask.id)}
                />
                <span className={subtask.completed ? 'line-through text-gray-400' : ''}>
                  {subtask.title}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

type TaskListProps = {
  tasks: Task[];
  onUpdateTasks: (updatedTasks: Task[]) => void;
  onToggleComplete: (taskId: number) => void;
  onDelete: (taskId: number) => void; 
  onRestore: (taskId: number) => void;
  onEdit: (task: Task) => void;
  onSelectTask: (taskId: number, taskTitle: string) => void;
  renderCustomItem?: (task: Task, index: number) => React.ReactNode;
};

const TaskList: React.FC<TaskListProps> = ({
  tasks,
  onUpdateTasks,
  onToggleComplete,
  onDelete,
  onRestore,
  onEdit,
  onSelectTask,
  renderCustomItem
}) => {
  const [pendingTasks, setPendingTasks] = useState<Task[]>([]);
  const [completedTasks, setCompletedTasks] = useState<Task[]>([]);
  const { state } = usePomodoro();
  
  // Function to sort tasks with current Pomodoro task first
  const sortTasksWithPomodoroFirst = useCallback((tasks: Task[], currentTaskId: number | null) => {
    if (!currentTaskId) return tasks;
    
    const currentTask = tasks.find(task => task.id === currentTaskId);
    if (!currentTask) return tasks;
    
    return [
      currentTask,
      ...tasks.filter(task => task.id !== currentTaskId)
    ];
  }, []);
  
  useEffect(() => {
    // Get all pending tasks
    const allPendingTasks = tasks.filter(task => !task.completed);
    
    // Prioritize the current Pomodoro task if it exists
    if (state.currentTaskId) {
      setPendingTasks(sortTasksWithPomodoroFirst(allPendingTasks, state.currentTaskId));
    } else {
      setPendingTasks(allPendingTasks);
    }
    
    setCompletedTasks(tasks.filter(task => task.completed));
  }, [tasks, state.currentTaskId, sortTasksWithPomodoroFirst]);
  
  // Custom handler for selecting a task for Pomodoro
  const handleSelectTaskForPomodoro = useCallback((taskId: number, taskTitle: string) => {
    // Call the parent's onSelectTask handler
    onSelectTask(taskId, taskTitle);
    
    // Re-sort the pending tasks to put this task first
    setPendingTasks(prevTasks => sortTasksWithPomodoroFirst(prevTasks, taskId));
  }, [onSelectTask, sortTasksWithPomodoroFirst]);
  
  const moveTask = useCallback(async (dragIndex: number, hoverIndex: number) => {
    // Only allow reordering of pending tasks
    const dragTask = pendingTasks[dragIndex];
    
    // Don't allow moving the current Pomodoro task from the first position
    if (dragIndex === 0 && state.currentTaskId === dragTask.id) {
      return;
    }
    
    const newTasks = [...pendingTasks];
    newTasks.splice(dragIndex, 1);
    newTasks.splice(hoverIndex, 0, dragTask);
    
    setPendingTasks(newTasks);
    
    // Update the parent component with the new task order
    const updatedTasks = [...newTasks, ...completedTasks];
    onUpdateTasks(updatedTasks);
    
    // 如果任务被拖拽到第一位，自动选择它用于番茄钟
    if (hoverIndex === 0) {
      handleSelectTaskForPomodoro(dragTask.id, dragTask.title);
    }

    // 调用后端 API 更新任务顺序
    try {
      await api.tasks.reorder(newTasks.map(task => task.id));
    } catch (error) {
      console.error('Failed to update task order:', error);
      // 如果 API 调用失败，可以选择是否要回滚 UI 状态
    }
  }, [pendingTasks, completedTasks, onUpdateTasks, handleSelectTaskForPomodoro, state.currentTaskId]);
  
  return (
    <DndProvider backend={HTML5Backend}>
      <div>
        <List
          dataSource={pendingTasks}
          renderItem={(task, index) => {
            // 如果提供了自定义渲染函数，优先使用
            if (renderCustomItem) {
              const customRendered = renderCustomItem(task, index);
              if (customRendered) {
                return customRendered;
              }
            }
            // 否则使用默认渲染组件
            return (
              <TaskItem
                key={task.id}
                task={task}
                index={index}
                moveTask={moveTask}
                onToggleComplete={onToggleComplete}
                onDelete={onDelete}
                onRestore={onRestore}
                onEdit={onEdit}
                onSelectForPomodoro={handleSelectTaskForPomodoro}
              />
            );
          }}
          style={{ marginBottom: '16px' }}
        />
        
        {completedTasks.length > 0 && (
          <div className="mt-4">
            <Divider orientation="left">
              <Text type="secondary">已完成</Text>
            </Divider>
            <List
              dataSource={completedTasks}
              renderItem={(task, index) => {
                // 同样检查已完成任务的自定义渲染
                if (renderCustomItem) {
                  const customRendered = renderCustomItem(task, index + pendingTasks.length);
                  if (customRendered) {
                    return customRendered;
                  }
                }
                // 否则使用默认渲染
                return (
                  <TaskItem
                    key={task.id}
                    task={task}
                    index={index + pendingTasks.length}
                    moveTask={moveTask}
                    onToggleComplete={onToggleComplete}
                    onDelete={onDelete}
                    onRestore={onRestore}
                    onEdit={onEdit}
                    onSelectForPomodoro={handleSelectTaskForPomodoro}
                  />
                );
              }}
            />
          </div>
        )}
      </div>
    </DndProvider>
  );
};

export default TaskList; 